import {
  Box,
  Chip,
  List,
  ListItemButton,
  ListItemText,
  ListSubheader,
} from "@mui/material";

import { NavLink, useLocation, useMatch, useResolvedPath } from "react-router-dom";

import { Permission } from "~/hooks/permissions";
import PermissionOrHide from "~/components/common/PermissionOrHide";
import VirtualVisitsCount from "./command/VirtualVisitsCount";
import { useTransitionAttention } from "~/providers/TransitionAttentionProvider";
import { usePredictContext } from "~/hooks/predict/usePredictContext";
import { useDischargeContext } from "~/hooks/discharge/useDischargeContext";
import { useOrgConfig } from "~/providers/OrgConfigProvider";
import { resolveIntakeServiceLine } from "~/utils/intakeServiceLine";
import { getSelectedProviderName } from "~/components/discharge/patient-detail/buildPlaceMatches";
import { PredictProgram } from "~/types/predict";
import { HideInHandsOn } from "~/features/hcah-hands-on";
import { ReactNode } from "react";

const drawerWidth = 240;

const sectionHeaderSx = {
  fontSize: "0.7rem",
  fontWeight: 700,
  letterSpacing: "0.08em",
  lineHeight: "32px",
  color: "text.secondary",
} as const;

type Props = {
  closeMenu: () => void
}

const MainMenu = ({ closeMenu }: Props) => {
  const { pathname } = useLocation();

  const onConfigTab = !!useMatch('operate-config/*');
  const onPredictConfigTab = !!useMatch('predict-config/*');

  const { unseenCount: transitionAttentionCount } = useTransitionAttention();
  const { patients: predictPatients } = usePredictContext();
  const { patients: dischargePatients } = useDischargeContext();
  const orgConfig = useOrgConfig();
  const predictIntakeCount = predictPatients.filter(p => p.selectedProgram === PredictProgram.HOSPITAL_AT_HOME && !p.scheduledAt).length;
  const coordinateIntakeCount = dischargePatients.filter(
    p => p.sentToIntakeAt && !p.scheduledAt && resolveIntakeServiceLine(getSelectedProviderName(p) ?? undefined, orgConfig.serviceLines) !== undefined,
  ).length;
  const hahIntakeCount = predictIntakeCount + coordinateIntakeCount;

  return <Box sx={{ overflow: "auto", width: drawerWidth }}>
    <List
      subheader={<ListSubheader disableSticky sx={sectionHeaderSx}>OPERATE</ListSubheader>}
    >
      <Link to="/command" onClick={closeMenu} primary="Command Center">
        <VirtualVisitsCount />
      </Link>

      <Link to="/intake" onClick={closeMenu} primary="Intake">
        {hahIntakeCount > 0 && (
          <Chip
            label={hahIntakeCount}
            size="small"
            color="primary"
            sx={{ height: 20, fontWeight: 600 }}
          />
        )}
      </Link>

      <PermissionOrHide permission={Permission.useVisitWorklist}>
        <Link to="/visit-alerts" onClick={closeMenu} primary="Visit Worklist" />
      </PermissionOrHide>

      <PermissionOrHide permission={Permission.manageSosAlerts}>
        <Link to="/sos-alerts" onClick={closeMenu} primary="SOS Alerts" />
      </PermissionOrHide>

      <Link to="/patients" onClick={closeMenu} primary="Patients" />

      <PermissionOrHide permission={Permission.manageProviderShifts}>
        <Link to="/availability" onClick={closeMenu} primary="Provider Availability" />
      </PermissionOrHide>

      <PermissionOrHide permission={Permission.useSupplies}>
        <Link to="/supplies" onClick={closeMenu} primary="Supplies Management" />
      </PermissionOrHide>

      <PermissionOrHide permission={Permission.viewKPIs}>
        <Link to="/kpis" onClick={closeMenu} primary="KPIs" />
      </PermissionOrHide>

      <HideInHandsOn>
        <Link to="/capacity-management" onClick={closeMenu} primary="Capacity Management" />
        <Link to="/capacity" onClick={closeMenu} primary="Branch Capacity" />
        <Link to="/referrals" onClick={closeMenu} primary="Referrals" />
        <Link to="/pending-admissions" onClick={closeMenu} primary="Pending admissions" />
        <Link to="/my-week" onClick={closeMenu} primary="My week" />
      </HideInHandsOn>
    </List>

    <HideInHandsOn>
      <List
        subheader={<ListSubheader disableSticky sx={sectionHeaderSx}>PREDICT</ListSubheader>}
      >
        <Link to="/predict/all-patients" selected={pathname === "/predict"} onClick={closeMenu} primary="All Patients" />
        <Link to="/predict/my-patients" onClick={closeMenu} primary="My Patients" />
        <Link to="/predict/unassigned-patients" onClick={closeMenu} primary="Unassigned Patients" />
        <Link to="/predict/kpis" onClick={closeMenu} primary="Analytics" />
      </List>

      <List
        subheader={<ListSubheader disableSticky sx={sectionHeaderSx}>COORDINATE</ListSubheader>}
      >
        <Link to="/discharge/all-patients" selected={pathname === "/discharge"} onClick={closeMenu} primary="All Patients" />
        <Link to="/discharge/my-patients" onClick={closeMenu} primary="My Patients" />
        <Link to="/discharge/unassigned-patients" onClick={closeMenu} primary="Unassigned Patients" />
      </List>

      <List
        subheader={<ListSubheader disableSticky sx={sectionHeaderSx}>TRANSITION</ListSubheader>}
      >
        <Link to="/transition/pipeline" selected={pathname === "/transition"} onClick={closeMenu} primary="Referral Pipeline">
          {transitionAttentionCount > 0 && (
            <Chip
              label={transitionAttentionCount}
              size="small"
              color="primary"
              sx={{ height: 18, fontSize: '0.7rem', mr: 1, '& .MuiChip-label': { px: 0.75 } }}
            />
          )}
        </Link>
      </List>
    </HideInHandsOn>

    <PermissionOrHide anyPermission={[Permission.manageOrgConfig, Permission.manageUsers, Permission.importUsers, Permission.manageApiKeys, Permission.manageTeams]}>
      <List
        subheader={<ListSubheader disableSticky sx={sectionHeaderSx}>ADMIN</ListSubheader>}
      >
        <PermissionOrHide permission={Permission.debugApps}>
          <Link to="/predict-config" selected={onPredictConfigTab} onClick={closeMenu} primary="Predict Configuration" />
        </PermissionOrHide>

        <PermissionOrHide anyPermission={[Permission.manageOrgConfig, Permission.manageTeams]}>
          <Link to="/operate-config" selected={onConfigTab} onClick={closeMenu} primary="Operate Configuration" />
        </PermissionOrHide>

        <PermissionOrHide permission={Permission.manageUsers}>
          <Link to="/users" onClick={closeMenu} primary="Users" />
        </PermissionOrHide>

        <PermissionOrHide permission={Permission.manageOrgConfig}>
          <Link to="/security" onClick={closeMenu} primary="Security" />
        </PermissionOrHide>

        <PermissionOrHide permission={Permission.manageApiKeys}>
          <Link to="/integrations" onClick={closeMenu} primary="Integrations" />
        </PermissionOrHide>
      </List>
    </PermissionOrHide>
  </Box>;
};
export default MainMenu;

const Link = ({ children, primary, onClick, selected, to }: {
  primary: string,
  onClick: () => void,
  to: string,
  selected?: boolean,
  children?: ReactNode,
}) => {
  if (selected === undefined) {
    const path = useResolvedPath(to);
    const match = useMatch(path.pathname);
    selected = !!match;
  }
  return <ListItemButton component={NavLink} sx={{ pl: 3 }} to={to} onClick={onClick} selected={selected}>
    <ListItemText primary={primary} />
    {children}
  </ListItemButton>;
}
